import { Link } from "react-router-dom";
import PageShell from "@/components/PageShell";
import PageMeta from "@/components/PageMeta";
import { ArrowRight, Lightning, Lock, EyeSlash, Users, Cloud, ShieldCheck } from "@phosphor-icons/react";

const Principle = ({ icon: Icon, title, children }) => (
  <div className="border-l-2 border-[#FF3366] pl-5 py-2">
    <div className="flex items-center gap-2 mb-2">
      <Icon size={16} className="text-[#FF3366]" weight="duotone" />
      <h4 className="font-bold tracking-tight">{title}</h4>
    </div>
    <p className="text-sm text-black/70 leading-relaxed">{children}</p>
  </div>
);

const H2 = ({ children }) => (
  <h2 className="text-2xl md:text-3xl tracking-tight mt-12 mb-4">{children}</h2>
);

const P = ({ children }) => (
  <p className="text-[16px] leading-[1.75] text-black/80 mb-4">{children}</p>
);

export default function About() {
  return (
    <PageShell
      eyebrow="About SendMe"
      title="Built so files move out of the way of the work."
      subtitle="SendMe is a no-friction file transfer tool — drop a file, share a 6-digit code, done. This is the story behind it, who we are, and what we believe."
    >
      <PageMeta
        title="About SendMe · Built so files move out of the way"
        description="SendMe is a friction-free file transfer built by a small independent team from Delhi. Our story, principles, and 2026 roadmap — including end-to-end encryption."
      />
      <div className="grid lg:grid-cols-[1.5fr_1fr] gap-10 items-start max-w-5xl">
        <div>
          <H2>Why we built SendMe</H2>
          <P>
            Sometime in 2025, we'd watched our fourth client meeting in a row derail because someone couldn't open a Dropbox link, or because Gmail bounced a 30 MB attachment, or because the recipient was on a phone and the Drive app demanded sign-in. The pattern was always the same: the file-transfer step was supposed to take 30 seconds and consistently took 15 minutes.
          </P>
          <P>
            We started SendMe because every existing tool either bloats with features nobody asked for, pushes ads into the download experience, demands accounts on both sides, or expires too fast to be useful. None of them got the basics right. None of them treated the handoff itself as the product — they all treated it as an upsell funnel to their storage subscription.
          </P>
          <P>
            We took the best ideas — a 6-digit code that's easy to read aloud, a QR for in-person handoffs, optional password protection for sensitive files, expiry by default — and removed everything else. No newsletters. No upsell modals. No 'you're 80% out of free quota'. Just a tool that does one thing well.
          </P>

          <H2>What we believe</H2>
          <div className="grid sm:grid-cols-2 gap-6 my-6">
            <Principle icon={Lightning} title="Friction is the enemy">
              The right number of steps to share a file is two: drop it, share the code. Every extra step costs trust.
            </Principle>
            <Principle icon={Lock} title="Privacy by default">
              Files expire automatically. We don't scan content for ads. We don't train models on what you send.
            </Principle>
            <Principle icon={EyeSlash} title="Accounts are optional">
              Receivers should never need to sign up. Senders sign in only if they want a history.
            </Principle>
            <Principle icon={Users} title="Teams are different">
              Workspaces are for ongoing collaboration; transfers are for one-off handoffs. We separate them deliberately.
            </Principle>
            <Principle icon={Cloud} title="Storage is rented, not owned">
              Permanent file storage is a different product. We're a transport. If you need long-term storage, use a storage tool.
            </Principle>
            <Principle icon={ShieldCheck} title="Honesty over polish">
              We tell you what we don't do as clearly as what we do. No E2EE-by-default yet — we'll say so. No SOC 2 yet — we'll say so.
            </Principle>
          </div>

          <H2>The principles in practice</H2>
          <P>
            Every product decision we make goes through one filter: does it make handing off a file faster or slower for the user who's already running late? If it makes things slower, it doesn't ship — even if it would help our metrics in the short term.
          </P>
          <P>
            That's why we don't have a referral programme baked into the upload flow, why we don't show a 'connect your Slack' modal after sign-in, and why the Pro upgrade prompt only appears on a single dedicated page. The upload experience is sacred — we keep it clean.
          </P>

          <H2>Who we are</H2>
          <P>
            SendMe is built by a small distributed team — a couple of engineers, a designer, and a part-time legal counsel. We started the project on weekends in early 2025 because the problem nagged at us. By mid-2025 we were full-time on it. We're based in India but think of the product as global from day one — most of our users are in India, the U.S., the U.K., and Brazil.
          </P>
          <P>
            We're independently funded so far. We may take outside investment if it helps us ship faster, but we're explicitly not interested in growth-at-all-costs funding that would push us toward dark patterns or invasive monetisation. The economics of a paid tier plus AdSense on the blog are sufficient.
          </P>

          <H2>The roadmap, plainly</H2>
          <P>
            Things we're working on, in rough order:
          </P>
          <ul className="list-disc pl-6 mb-6 space-y-2 text-[16px] leading-relaxed text-black/80">
            <li><strong>End-to-end encryption</strong> with the key in the URL fragment — late 2026.</li>
            <li><strong>Public REST API</strong> for Pro subscribers — Q3 2026.</li>
            <li><strong>Mobile PWA</strong> with Add-to-Home-Screen installability — Q2 2026.</li>
            <li><strong>Multi-region origin storage</strong> for better speed outside India — end of 2026.</li>
            <li><strong>Resumable downloads</strong> for very large files — once our storage backend supports HTTP Range — TBD.</li>
            <li><strong>SOC 2 Type I</strong> certification — 2027.</li>
          </ul>

          <H2>How we make money</H2>
          <P>
            SendMe Pro is ₹699 per month and gives you larger files, longer expiry, transfer history, and 100 GB of personal cloud storage. The team plan is ₹24/month per seat for shared workspaces. AdSense on the blog covers a tiny fraction of the cost of running the free tier — most of the free-tier load is paid for by Pro subscribers, which is a model we like because it means we never have an incentive to degrade the free experience to force upgrades.
          </P>

          <H2>Get in touch</H2>
          <P>
            For product feedback, write to <a className="underline" href="mailto:hello@sendme.co.in">hello@sendme.co.in</a>. For support questions, <a className="underline" href="mailto:support@sendme.co.in">support@sendme.co.in</a>. For security reports, <a className="underline" href="mailto:security@sendme.co.in">security@sendme.co.in</a>. We read every email, and replies typically come from the person who built the thing you're writing about.
          </P>
        </div>

        <aside className="brut-card p-6 sticky top-24">
          <div className="aspect-[4/5] overflow-hidden border border-black mb-5">
            <img
              src="https://images.unsplash.com/photo-1517292987719-0369a794ec0f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
              alt="A minimalist workspace"
              className="w-full h-full object-cover grayscale contrast-110"
            />
          </div>
          <div className="small-caps mb-3">Quick facts</div>
          <ul className="space-y-2 text-sm text-black/75 font-mono">
            <li>· Founded: 2025</li>
            <li>· Team size: small, deliberate</li>
            <li>· HQ: Delhi, India</li>
            <li>· Storage: Asia-South (Mumbai)</li>
            <li>· Independent — no VC yet</li>
            <li>· 100% web-based, no app required</li>
          </ul>

          <div className="mt-6 small-caps">Explore</div>
          <ul className="mt-3 space-y-2 text-sm">
            <li><Link className="hover:text-[#FF3366] inline-flex items-center gap-1" to="/security">Security model <ArrowRight size={12}/></Link></li>
            <li><Link className="hover:text-[#FF3366] inline-flex items-center gap-1" to="/use-cases">Real use cases <ArrowRight size={12}/></Link></li>
            <li><Link className="hover:text-[#FF3366] inline-flex items-center gap-1" to="/help">Help center <ArrowRight size={12}/></Link></li>
            <li><Link className="hover:text-[#FF3366] inline-flex items-center gap-1" to="/blog">Blog <ArrowRight size={12}/></Link></li>
          </ul>
        </aside>
      </div>
    </PageShell>
  );
}
